import React, { useState, useEffect } from "react";
import styles from "./Sidebar.module.sass";
import { Link, NavLink, useLocation } from "react-router-dom";
import cn from "classnames";
import Icon from "../Icon";
import Theme from "../Theme";
import Dropdown from "./Dropdown";
import Image from "../Image";
import { useAuth } from "../../contexts/AuthContext";
import { getUnassignedProspectsCount } from "../../services/prospect.service";
import { getUnassignedProspectCompaniesCount } from "../../services/prospectCompany.service";
import { getUnassignedOffersCount } from "../../services/offerAdmin.service";
import { getUnreadNotificationsCount, initializeUnreadNotificationsCount } from "../../services/notification.service";
import { getCurrentPartnerInfo } from "../../services/partner.service";
import eventEmitter from "../../services/event.service";
import Help from "./Help";

// Navigation pour les SuperAdmin
const superAdminNavigation = [
    {
        title: "Dashboard",
        icon: "home",
        url: "/dashboard",
    },
    {
        title: "Partenaires",
        icon: "store",
        url: "/partners",
    },
    {
        title: "Tickets",
        icon: "message",
        url: "/contact-request",
    },
];

// Navigation pour les administrateurs
const adminNavigation = [

    {
        title: "Dashboard",
        slug: "dashboard",
        icon: "home",
        dropdown: [
            {
                title: "Dashboard",
                url: "/admin/dashboard",
            },
            {
                title: "Dossiers",
                url: "/admin/folders",
            },
            {
                title: "Facturation",
                url: "/admin/invoice-details",
            },
            {
                title: "Export",
                url: "/admin/extract",
            },
        ],
    },
    {
        title: "Prospects",
        slug: "prospects",
        icon: "lightning",
        dropdown: [
            {
                title: "Dashboard",
                url: "/admin/prospect-dashboard",
            },
            {
                title: "Etudiants",
                colorCounter: "#B5E4CA",
                url: "/admin/prospects_student",
            },
            {
                title: "Entreprises",
                colorCounter: "#B5E4CA",
                url: "/admin/prospects_company",
            },
            {
                title: "Offres d'emploi",
                colorCounter: "#B5E4CA",
                url: "/admin/alternance-offers",
            },
        ],
    },
    {
        title: "Emargement",
        slug: "emargement",
        icon: "edit",
        dropdown: [
            {
                title: "Dashboard",
                url: "/admin/sessions-formation-dashboard",
            },
            {
                title: "Feuilles de présence",
                colorCounter: "#B5E4CA",
                url: "/admin/sessions-formation",
            },
            {
                title: "Groupes",
                colorCounter: "#B5E4CA",
                url: "/admin/session-groups",
            },
        ],
    },
    {
        title: "Élèves",
        icon: "profile-circle",
        url: "/admin/students",
    },
    {
        title: "Entreprises",
        icon: "store",
        url: "/admin/companies",
    },
    {
        title: "Centres de formation",
        icon: "store-fill",
        url: "/admin/centers",
    },
    {
        title: "Formations",
        icon: "grid",
        url: "/admin/formations",
    },
];

// Navigation pour les étudiants
const studentNavigation = [
    {
        title: "Dashboard",
        icon: "store",
        url: "/student/dashboard",
    },
    {
        title: "Mon compte",
        icon: "profile-circle",
        url: "/student/account",
    },
    
    {
        title: "Notifications",
        icon: "notification",
        url: "/student/notifications",
    },
    {
        title: "Planning",
        icon: "grid",
        url: "/student/planning",
    },
];

// Navigation pour les entreprises
const companyNavigation = [
    {
        title: "Dashboard",
        icon: "store",
        url: "/company/dashboard",
    },
    {
        title: "Entreprise",
        icon: "profile-circle",
        url: "/company/account",
    },
    {
        title: "Offres d'alternance",
        slug: "products",
        icon: "grid",
        add: true,
        dropdown: [
            {
                title: "Candidatures",
                url: "/company/alternance-offers",
                counter: "2",
                colorCounter: "#FFBC99",
            },
        ],
    },
    {
        title: "Notifications",
        icon: "notification",
        url: "/company/notifications",
    },
];

// Navigation pour les commerciaux
const commercialNavigation = [
    {
        title: "Prospects",
        slug: "prospects",
        icon: "lightning",
        dropdown: [
            {
                title: "Dashboard",
                url: "/commercial/prospect-dashboard",
            },
            {
                title: "Etudiants",
                colorCounter: "#B5E4CA",
                url: "/commercial/prospects_student",
            },
            {
                title: "Entreprises",
                colorCounter: "#B5E4CA",
                url: "/commercial/prospects_company",
            },
            {
                title: "Offres d'emploi",
                colorCounter: "#B5E4CA",
                url: "/commercial/alternance-offers",
            },
        ],
    },
];

// Navigation pour les responsables pédagogiques
const pedagogicalNavigation = [
    {
        title: "Dashboard",
        slug: "dashboard",
        icon: "home",
        dropdown: [
            {
                title: "Dashboard",
                url: "/pedagogical/dashboard",
            },
            {
                title: "Dossiers",
                url: "/pedagogical/folders",
            },
            {
                title: "Facturation",
                url: "/pedagogical/invoice-details",
            },
        ],
    },
    {
        title: "Élèves",
        icon: "profile-circle",
        url: "/pedagogical/students",
    },
    {
        title: "Entreprises",
        icon: "store",
        url: "/pedagogical/companies",
    },
    {
        title: "Centres de formation",
        icon: "store-fill",
        url: "/pedagogical/centers",
    },
    {
        title: "Formations",
        icon: "grid",
        url: "/pedagogical/formations",
    }
];

// Navigation pour les enseignants
const teacherNavigation = [
    {
        title: "Feuilles de présence",
        icon: "edit",
        url: "/teacher/sessions-formation",
    },
];



const Sidebar = ({ className, onClose }) => {
    const [visibleHelp, setVisibleHelp] = useState(false);
    const [visible, setVisible] = useState(false);
    const { isAdmin, isStudent, isCompany, isCommercial, isPedagogical, isTeacher, isSuperAdmin, currentUser } = useAuth();
    const [unassignedProspectsCount, setUnassignedProspectsCount] = useState(0);
    const [unassignedProspectCompaniesCount, setUnassignedProspectCompaniesCount] = useState(0);
    const [unassignedOffersCount, setUnassignedOffersCount] = useState(0);
    const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
    const [partnerLogo, setPartnerLogo] = useState(null);
    const { pathname } = useLocation();

    // Fonction utilitaire pour construire l'URL du logo partenaire
    const getPartnerLogoUrl = (partner) => {
        if (!partner || !partner.logo) {
            return "/images/logo.png"; // Logo par défaut
        }
        
        // Si le logo commence par 'http', c'est une URL complète
        if (partner.logo.startsWith('http')) {
            return partner.logo;
        }
        
        // Sinon, construire l'URL PocketBase
        const REACT_APP_POCKETBASE_URL = process.env.REACT_APP_POCKETBASE_URL || 'http://127.0.0.1:8090';
        return `${REACT_APP_POCKETBASE_URL}/api/files/partners/${partner.id}/${partner.logo}`;
    };

    useEffect(() => {
        const fetchCount = () => {
            // Récupérer le compteur pour tous les rôles qui ont accès aux prospects
            if (isAdmin || isCommercial || isPedagogical) {
                getUnassignedProspectsCount()
                    .then(setUnassignedProspectsCount)
                    .catch(err => console.error("Failed to fetch prospect count", err));
            }
        };

        fetchCount(); // Appel initial

        // S'abonner à l'événement
        eventEmitter.subscribe('prospectsUpdated', fetchCount);

        // Se désabonner lors du nettoyage
        return () => {
            eventEmitter.unsubscribe('prospectsUpdated', fetchCount);
        };
    }, [isAdmin, isCommercial, isPedagogical]);

    // useEffect pour le compteur des prospects entreprises
    useEffect(() => {
        const fetchCompanyCount = () => {
            // Récupérer le compteur pour tous les rôles qui ont accès aux prospects entreprises
            if (isAdmin || isCommercial || isPedagogical) {
                getUnassignedProspectCompaniesCount()
                    .then(setUnassignedProspectCompaniesCount)
                    .catch(err => console.error("Failed to fetch unassigned prospect company count", err));
            }
        };

        fetchCompanyCount(); // Appel initial

        // S'abonner à l'événement
        eventEmitter.subscribe('prospectCompaniesUpdated', fetchCompanyCount);

        // Se désabonner lors du nettoyage
        return () => {
            eventEmitter.unsubscribe('prospectCompaniesUpdated', fetchCompanyCount);
        };
    }, [isAdmin, isCommercial, isPedagogical]);

    // useEffect pour le compteur des offres d'emploi non assignées
    useEffect(() => {
        const fetchOffersCount = () => {
            // Récupérer le compteur pour tous les rôles qui ont accès aux offres d'emploi
            if (isAdmin || isCommercial || isPedagogical) {
                getUnassignedOffersCount()
                    .then(setUnassignedOffersCount)
                    .catch(err => console.error("Failed to fetch unassigned offers count", err));
            }
        };

        fetchOffersCount(); // Appel initial

        // S'abonner à l'événement
        eventEmitter.subscribe('offersUpdated', fetchOffersCount);

        // Se désabonner lors du nettoyage
        return () => {
            eventEmitter.unsubscribe('offersUpdated', fetchOffersCount);
        };
    }, [isAdmin, isCommercial, isPedagogical]);

    useEffect(() => {
        const initializeNotificationsCounter = async () => {
            try {
                const count = await initializeUnreadNotificationsCount();
                setUnreadNotificationsCount(count);
            } catch (error) {
            }
        };

        const handleCounterUpdate = (newCount) => {
            setUnreadNotificationsCount(newCount);
        };

        initializeNotificationsCounter();

        // S'abonner à l'événement de mise à jour du compteur
        eventEmitter.subscribe('notificationCounterUpdated', handleCounterUpdate);

        return () => {
            eventEmitter.unsubscribe('notificationCounterUpdated', handleCounterUpdate);
        };
    }, []);

    // Récupérer les informations du partenaire pour le logo
    useEffect(() => {
        const fetchPartnerInfo = async () => {
            try {
                // Ne récupérer le logo que si l'utilisateur a un partner_id
                if (currentUser && currentUser.partner_id) {
                    const partnerInfo = await getCurrentPartnerInfo();
                    if (partnerInfo) {
                        const logoUrl = getPartnerLogoUrl(partnerInfo);
                        setPartnerLogo(logoUrl);
                    } else {
                        setPartnerLogo("/images/logo.png");
                    }
                } else {
                    // Si pas de partner_id, utiliser le logo par défaut
                    setPartnerLogo("/images/logo.png");
                }
            } catch (error) {
                // En cas d'erreur, utiliser le logo par défaut
                setPartnerLogo("/images/logo.png");
            }
        };

        // Ne récupérer le logo que si l'utilisateur est connecté
        if (currentUser) {
            fetchPartnerInfo();
        } else {
            setPartnerLogo("/images/logo.png");
        }
    }, [currentUser]);

    // Sélectionner la navigation en fonction du rôle de l'utilisateur
    let navigation = [];

    if (isSuperAdmin) {
        navigation = [...superAdminNavigation];
    } else if (isAdmin) {
        // Créer une copie profonde pour permettre la modification
        navigation = JSON.parse(JSON.stringify(adminNavigation));
        const prospectItem = navigation
            .find(i => i.title === 'Prospects')
            ?.dropdown?.find(i => i.url === '/admin/prospects_student');

        if (prospectItem) {
            if (unassignedProspectsCount > 0) {
                prospectItem.counter = unassignedProspectsCount.toString();
            } else {
                delete prospectItem.counter;
            }
        }

        // Gestion du compteur pour les prospects entreprises
        const prospectCompanyItem = navigation
            .find(i => i.title === 'Prospects')
            ?.dropdown?.find(i => i.url === '/admin/prospects_company');

        if (prospectCompanyItem) {
            if (unassignedProspectCompaniesCount > 0) {
                prospectCompanyItem.counter = unassignedProspectCompaniesCount.toString();
            } else {
                delete prospectCompanyItem.counter;
            }
        }

        // Gestion du compteur pour les offres d'emploi non assignées
        const offersItem = navigation
            .find(i => i.title === 'Prospects')
            ?.dropdown?.find(i => i.url === '/admin/alternance-offers');

        if (offersItem) {
            if (unassignedOffersCount > 0) {
                offersItem.counter = unassignedOffersCount.toString();
            } else {
                delete offersItem.counter;
            }
        }

    } else if (isStudent) {
        // Créer une copie profonde pour permettre la modification
        navigation = JSON.parse(JSON.stringify(studentNavigation));
        const notificationItem = navigation.find(i => i.url === '/student/notifications');
        
        if (notificationItem) {
            if (unreadNotificationsCount > 0) {
                notificationItem.counter = unreadNotificationsCount.toString();
                notificationItem.colorCounter = "#B5E4CA";
            } else {
                delete notificationItem.counter;
                delete notificationItem.colorCounter;
            }
        }
    } else if (isCommercial) {
        // Créer une copie profonde pour permettre la modification
        navigation = JSON.parse(JSON.stringify(commercialNavigation));
        
        // Gestion du compteur pour les prospects étudiants
        const prospectStudentItem = navigation
            .find(i => i.slug === 'prospects')
            ?.dropdown?.find(i => i.url === '/commercial/prospects_student');

        if (prospectStudentItem) {
            if (unassignedProspectsCount > 0) {
                prospectStudentItem.counter = unassignedProspectsCount.toString();
            } else {
                delete prospectStudentItem.counter;
            }
        }

        // Gestion du compteur pour les prospects entreprises
        const prospectCompanyItem = navigation
            .find(i => i.slug === 'prospects')
            ?.dropdown?.find(i => i.url === '/commercial/prospects_company');

        if (prospectCompanyItem) {
            if (unassignedProspectCompaniesCount > 0) {
                prospectCompanyItem.counter = unassignedProspectCompaniesCount.toString();
            } else {
                delete prospectCompanyItem.counter;
            }
        }

        // Gestion du compteur pour les offres d'emploi non assignées
        const offersItem = navigation
            .find(i => i.slug === 'prospects')
            ?.dropdown?.find(i => i.url === '/commercial/alternance-offers');

        if (offersItem) {
            if (unassignedOffersCount > 0) {
                offersItem.counter = unassignedOffersCount.toString();
            } else {
                delete offersItem.counter;
            }
        }
        
        // Gestion du compteur de notifications
        const notificationItem = navigation.find(i => i.url === '/commercial/notifications');
        
        if (notificationItem) {
            if (unreadNotificationsCount > 0) {
                notificationItem.counter = unreadNotificationsCount.toString();
                notificationItem.colorCounter = "#B5E4CA";
            } else {
                delete notificationItem.counter;
                delete notificationItem.colorCounter;
            }
        }
    } else if (isPedagogical) {
        // Créer une copie profonde pour permettre la modification
        navigation = JSON.parse(JSON.stringify(pedagogicalNavigation));
        
        // Gestion du compteur de prospects non assignés
        const prospectItem = navigation
            .find(i => i.slug === 'dashboard')
            ?.dropdown?.find(i => i.url === '/pedagogical/prospects');

        if (prospectItem) {
            if (unassignedProspectsCount > 0) {
                prospectItem.counter = unassignedProspectsCount.toString();
            } else {
                delete prospectItem.counter;
            }
        }
        
        // Gestion du compteur de notifications
        const notificationItem = navigation.find(i => i.url === '/pedagogical/notifications');
        
        if (notificationItem) {
            if (unreadNotificationsCount > 0) {
                notificationItem.counter = unreadNotificationsCount.toString();
                notificationItem.colorCounter = "#B5E4CA";
            } else {
                delete notificationItem.counter;
                delete notificationItem.colorCounter;
            }
        }
    } else if (isTeacher) {
        // Navigation pour les enseignants
        navigation = JSON.parse(JSON.stringify(teacherNavigation));
    } else if (isCompany) {
        // Créer une copie profonde pour permettre la modification
        navigation = JSON.parse(JSON.stringify(companyNavigation));
        const notificationItem = navigation.find(i => i.url === '/company/notifications');
        
        if (notificationItem) {
            if (unreadNotificationsCount > 0) {
                notificationItem.counter = unreadNotificationsCount.toString();
                notificationItem.colorCounter = "#B5E4CA";
            } else {
                delete notificationItem.counter;
                delete notificationItem.colorCounter;
            }
        }
    }

    return (
        <>
            <div
                className={cn(styles.sidebar, className, {
                    [styles.active]: visible,
                })}
            >
                <button className={styles.close} onClick={onClose}>
                    <Icon name="close" size="24" />
                </button>
                <Link to="/" className={styles.logo} onClick={onClose}>
                    <Image
                        className={styles.pic}
                        src={partnerLogo || "/images/logo.png"}
                        srcDark={partnerLogo || "/images/logo.png"}
                        alt="Logo du partenaire"
                    />
                </Link>
                <div className={styles.menu}>
                    {navigation.map((x, index) =>
                        x.url ? (
                            <NavLink
                                className={cn(styles.item, {
                                    [styles.active]: pathname === x.url,
                                })}
                                to={x.url}
                                key={index}
                                onClick={onClose}
                            >
                                <Icon name={x.icon} size="24" />
                                {x.title}
                                {x.counter && (
                                    <span className={styles.counter} style={{ backgroundColor: x.colorCounter }}>
                                        {x.counter}
                                    </span>
                                )}
                            </NavLink>
                        ) : (
                            <Dropdown
                                className={styles.dropdown}
                                visibleSidebar={visible}
                                setValue={setVisible}
                                key={index}
                                item={x}
                                onClose={onClose}
                            />
                        )
                    )}
                </div>
                <button
                    className={styles.toggle}
                    onClick={() => setVisible(!visible)}
                >
                    <Icon name="arrow-right" size="24" />
                    <Icon name="close" size="24" />
                </button>
                <div className={styles.foot}>
                    {isAdmin && (
                        <button
                            className={styles.link}
                            onClick={() => setVisibleHelp(true)}
                        >
                            <Icon name="help" size="24" />
                            Paramètres & Aides
                        </button>
                    )}
                    <Theme className={styles.theme} visibleSidebar={visible} />
                </div>
            </div>
            <Help
                visible={visibleHelp}
                setVisible={setVisibleHelp}
                onClose={onClose}
            />

            <div
                className={cn(styles.overlay, { [styles.active]: visible })}
                onClick={() => setVisible(false)}
            ></div>
        </>
    );
};

export default Sidebar;
